import React from 'react';
import { TabsProps } from 'antd';

import CvssMetricsV2Panel from '@totem/components/software/cves/cvssMetricsV2Panel';
import CvssMetricsV3Panel from '@totem/components/software/cves/cvssMetricsV3Panel';
import CvssMetricsV4Panel from '@totem/components/software/cves/cvssMetricsV4Panel';
import { CVERecord } from '@totem/components/software/cves/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

type Props = {
  cve: CVERecord;
};

// Primary (NVD) assessments sort before Secondary (CNA) ones.
const byType = <T extends { type: string }>(metrics?: T[] | null): T[] =>
  (metrics ?? [])
    .slice()
    .sort((compA, compB) => sortStringAscending(compA.type, compB.type));

const CvssTabs = ({ cve }: Props) => {
  const { metrics } = cve.cve.raw_nvd_data;

  const getTabItems = () => {
    const items: TabsProps['items'] = [];

    const metricsV40 = byType(metrics.cvssMetricV40);
    if (metricsV40.length > 0) {
      items.push({
        key: 'v40',
        label: 'CVSS v4.0',
        children: metricsV40.map((metric, idx) => (
          <div key={idx}>
            {idx > 0 && <hr />}
            <CvssMetricsV4Panel
              metricsV4={metric}
              showType={metricsV40.length > 1}
            />
          </div>
        )),
      });
    }

    const metricsV3 = [
      {
        key: 'v31',
        title: 'CVSS v3.1',
        metrics: byType(metrics.cvssMetricV31),
      },
      {
        key: 'v30',
        title: 'CVSS v3.0',
        metrics: byType(metrics.cvssMetricV30),
      },
    ];
    metricsV3
      .filter((version) => version.metrics.length > 0)
      .forEach((version) => {
        items.push({
          key: version.key,
          label: version.title,
          children: version.metrics.map((metric, idx) => (
            <div key={idx}>
              {idx > 0 && <hr />}
              <CvssMetricsV3Panel
                metricsV3={metric}
                showType={version.metrics.length > 1}
                title={version.title}
              />
            </div>
          )),
        });
      });

    const metricsV2 = byType(metrics.cvssMetricV2);
    if (metricsV2.length > 0) {
      items.push({
        key: 'v2',
        label: 'CVSS v2',
        children: metricsV2.map((metric, idx) => (
          <div key={idx}>
            {idx > 0 && <hr />}
            <CvssMetricsV2Panel
              metricsV2={metric}
              showType={metricsV2.length > 1}
            />
          </div>
        )),
      });
    }

    return items;
  };

  return <IBTabs items={getTabItems()} />;
};

export default CvssTabs;
